import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  Edit2, PackageCheck, Trash2, Wrench,
  AlertTriangle, ShieldAlert, CheckCircle,
} from "lucide-react";
import { assetApi } from "../api";
import type { MarkIncidentRequest } from "../types";
import { useAsyncData } from "../hooks";
import {
  AssetError, AssetLoading, AssetStatusBadge, ConditionBadge,
  BackBtn, Btn, Card, CardHeader, PageHeader,
  Table, TBody, TD, TH, THead, TR, EmptyState,
  fmt, fmtDate,
} from "../components/AssetUI";
import { useAuth } from "#/context/AuthProvider";

function Detail({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">{label}</p>
      <div className="mt-1 text-sm font-medium text-slate-800">{value ?? <span className="text-slate-300">—</span>}</div>
    </div>
  );
}

// ─── Incident modal ───────────────────────────────────────────────────────────

type IncidentType = "lost" | "damaged";

function IncidentModal({
  type, assetName, onClose, onSaved,
}: {
  type: IncidentType;
  assetName: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const assetId = Number(useParams().id);
  const [form, setForm]   = useState<MarkIncidentRequest>({ reportedBy: "", incidentDate: new Date().toISOString().slice(0,10), incidentNotes: "" });
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true); setError(null);
    try {
      const payload: MarkIncidentRequest = {
        reportedBy:    form.reportedBy    || undefined,
        incidentDate:  form.incidentDate  || undefined,
        incidentNotes: form.incidentNotes || undefined,
      };
      if (type === "lost")    await assetApi.markAsLost(assetId, payload);
      else                    await assetApi.markAsDamaged(assetId, payload);
      onSaved();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Action failed");
    } finally {
      setSaving(false);
    }
  }

  const isDanger = type === "lost";
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <form onSubmit={submit} className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl space-y-4">
        <div className="flex items-center gap-3">
          {isDanger
            ? <ShieldAlert className="h-6 w-6 text-red-500" />
            : <AlertTriangle className="h-6 w-6 text-amber-500" />}
          <h2 className="text-lg font-bold text-slate-800">
            Mark as {type === "lost" ? "Lost" : "Damaged"}
          </h2>
        </div>
        <p className="text-sm text-slate-500">
          Asset: <span className="font-semibold text-slate-700">{assetName}</span>
        </p>

        <div className="space-y-3">
          <Field label="Reported By">
            <input className={inp} placeholder="Name of person reporting"
              value={form.reportedBy ?? ""}
              onChange={(e) => setForm((f) => ({ ...f, reportedBy: e.target.value }))} />
          </Field>
          <Field label="Incident Date">
            <input type="date" className={inp}
              value={form.incidentDate ?? ""}
              onChange={(e) => setForm((f) => ({ ...f, incidentDate: e.target.value }))} />
          </Field>
          <Field label="Incident Notes">
            <textarea rows={3} className={`${inp} resize-none`}
              placeholder="Describe what happened…"
              value={form.incidentNotes ?? ""}
              onChange={(e) => setForm((f) => ({ ...f, incidentNotes: e.target.value }))} />
          </Field>
        </div>

        {error && <p className="rounded-xl bg-red-50 px-3 py-2 text-sm text-red-600">{error}</p>}

        {type === "lost" && (
          <p className="text-xs text-red-500">
            ⚠ This will set status to LOST and clear the current assignment.
          </p>
        )}

        <div className="flex justify-end gap-3 pt-1">
          <Btn variant="ghost" size="md" type="button" onClick={onClose}>Cancel</Btn>
          <Btn
            variant={isDanger ? "danger" : "amber"}
            size="md"
            type="submit"
            loading={saving}
            loadingText="Saving…"
          >
            Confirm
          </Btn>
        </div>
      </form>
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function AssetDetailPage() {
  const { id } = useParams();
  const assetId = Number(id);
  const [incidentModal, setIncidentModal] = useState<IncidentType | null>(null);
  const { hasRole, hasAnyPermission } = useAuth();
  const isAdmin = hasRole("SUPER_ADMIN") || hasRole("ADMIN");
  const canEdit    = isAdmin || hasAnyPermission(["ASSET_EDIT", "ASSET_MANAGE"]);
  const canAssign  = isAdmin || hasAnyPermission(["ASSET_ASSIGN", "ASSET_MANAGE"]);
  const canDispose = isAdmin || hasAnyPermission(["ASSET_DISPOSE", "ASSET_MANAGE"]);

  const { data, loading, error, reload } = useAsyncData(() => assetApi.getAsset(assetId), [assetId]);
  const assignments = useAsyncData(
    () => assetApi.assetAssignmentHistory(assetId, 0, 10),
    [assetId],
  );
  const maintenance = useAsyncData(
    () => assetApi.listMaintenance(0, 10, { assetId }),
    [assetId],
  );

  if (loading) return <AssetLoading label="Loading asset…" />;
  if (error)   return <AssetError message={error} onRetry={reload} />;
  if (!data)   return <AssetError message="Asset not found." />;

  const isActive   = !["DISPOSED", "LOST"].includes(data.status);
  const isPendingDisposal = data.status === "PENDING_DISPOSAL";
  const hasIncident = data.reportedBy || data.incidentDate || data.incidentNotes;

  return (
    <div className="space-y-5">
      <PageHeader
        breadcrumb="Asset Management · Asset Register"
        title={data.name}
        description={`${data.assetNumber} · ${data.categoryName}`}
        actions={
          <div className="flex flex-wrap gap-2">
            <BackBtn to="/assets/list" label="Asset Register" />
            {canEdit && (
              <Link to={`/assets/${assetId}/edit`}>
                <Btn variant="secondary" icon={Edit2}>Edit</Btn>
              </Link>
            )}
            {canAssign && data.status === "AVAILABLE" && (
              <Btn variant="secondary" icon={PackageCheck}>Assign</Btn>
            )}
            {canDispose && isActive && !isPendingDisposal && (
              <Btn variant="dangerOutline" icon={Trash2}
                onClick={() => assetApi.flagForDisposal(assetId, true).then(reload)}>
                Flag for Disposal
              </Btn>
            )}
            {canEdit && isActive && data.status !== "LOST" && (
              <Btn variant="danger" size="md" icon={ShieldAlert}
                onClick={() => setIncidentModal("lost")}>
                Mark Lost
              </Btn>
            )}
            {canEdit && isActive && data.status !== "DAMAGED" && (
              <Btn variant="amber" size="md" icon={AlertTriangle}
                onClick={() => setIncidentModal("damaged")}>
                Mark Damaged
              </Btn>
            )}
          </div>
        }
      />

      {/* Flagged banner */}
      {data.flaggedForDisposal && (
        <div className="flex items-center gap-3 rounded-2xl border border-red-200 bg-red-50 px-5 py-3">
          <Trash2 className="h-5 w-5 shrink-0 text-red-500" />
          <p className="font-semibold text-red-800">This asset is flagged for disposal.</p>
        </div>
      )}

      {/* Incident banner */}
      {hasIncident && (
        <div className={`flex items-start gap-3 rounded-2xl border px-5 py-4 ${data.status === "LOST" ? "border-red-200 bg-red-50" : "border-amber-200 bg-amber-50"}`}>
          {data.status === "LOST"
            ? <ShieldAlert className="mt-0.5 h-5 w-5 shrink-0 text-red-500" />
            : <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-500" />}
          <div className="text-sm">
            <p className="font-semibold text-slate-800">
              {data.status === "LOST" ? "Asset reported lost" : "Asset reported damaged"}
              {data.incidentDate ? ` on ${fmtDate(data.incidentDate)}` : ""}
              {data.reportedBy ? ` by ${data.reportedBy}` : ""}
            </p>
            {data.incidentNotes && <p className="mt-0.5 text-slate-600">{data.incidentNotes}</p>}
          </div>
        </div>
      )}

      {/* KPI cards */}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {[
          { label: "Status",         value: <AssetStatusBadge value={data.status} /> },
          { label: "Condition",      value: <ConditionBadge value={data.condition} /> },
          { label: "Purchase Cost",  value: <span className="text-xl font-bold text-slate-900">{fmt(data.purchaseCost)}</span> },
          { label: "Current Value",  value: <span className="text-xl font-bold text-emerald-700">{fmt(data.currentValue)}</span> },
        ].map((c) => (
          <div key={c.label} className="rounded-2xl border bg-white p-4 shadow-sm">
            <p className="text-xs font-semibold uppercase tracking-widest text-slate-400">{c.label}</p>
            <div className="mt-2">{c.value}</div>
          </div>
        ))}
      </div>

      {/* Asset details */}
      <Card noPad>
        <CardHeader title="Asset Details" />
        <div className="grid gap-x-8 gap-y-5 p-5 text-sm sm:grid-cols-2 lg:grid-cols-3">
          <Detail label="Asset Number"   value={<span className="font-mono text-xs font-semibold text-indigo-700">{data.assetNumber}</span>} />
          <Detail label="Category"       value={data.categoryName} />
          <Detail label="Location"       value={data.locationName} />
          <Detail label="Serial Number"  value={data.serialNumber} />
          <Detail label="Manufacturer"   value={data.manufacturer} />
          <Detail label="Brand"          value={data.brand} />
          <Detail label="Model"          value={data.model} />
          <Detail label="Purchase Date"  value={fmtDate(data.purchaseDate)} />
          <Detail label="Warranty Expiry" value={fmtDate(data.warrantyExpiry)} />
          <Detail label="Supplier"        value={data.supplier} />
          <Detail label="Invoice No."     value={data.invoiceNumber} />
          <Detail label="Dep. Method"     value={data.depreciationMethod} />
          <Detail label="Assigned To"     value={data.assignedEmployeeName} />
          <Detail label="Assigned At"     value={fmtDate(data.assignedAt)} />
          {data.notes && (
            <div className="sm:col-span-2 lg:col-span-3">
              <Detail label="Notes" value={data.notes} />
            </div>
          )}
        </div>
      </Card>

      {/* Assignment history */}
      <Card noPad>
        <CardHeader title="Assignment History" subtitle="Full history of who held this asset" />
        {assignments.loading ? <AssetLoading /> : (
          <Table>
            <THead>
              <TH>Employee</TH><TH>Assigned</TH><TH>Returned</TH>
              <TH>Cond. In</TH><TH>Cond. Out</TH><TH>Purpose</TH>
            </THead>
            <TBody>
              {!(assignments.data?.content.length)
                ? <tr><td colSpan={6}><EmptyState icon={PackageCheck} title="No assignment history" /></td></tr>
                : assignments.data!.content.map((a) => (
                  <TR key={a.id}>
                    <TD><div className="font-semibold text-slate-800">{a.employeeName}</div></TD>
                    <TD muted>{fmtDate(a.assignedDate)}</TD>
                    <TD muted>{a.returnedDate ? fmtDate(a.returnedDate) : <span className="inline-flex items-center gap-1 text-xs text-emerald-600 font-semibold"><CheckCircle className="h-3 w-3"/>Active</span>}</TD>
                    <TD><ConditionBadge value={a.conditionAtAssignment} /></TD>
                    <TD>{a.conditionAtReturn ? <ConditionBadge value={a.conditionAtReturn} /> : <span className="text-slate-300">—</span>}</TD>
                    <TD muted>{a.purpose ?? "—"}</TD>
                  </TR>
                ))}
            </TBody>
          </Table>
        )}
      </Card>

      {/* Maintenance log */}
      <Card noPad>
        <CardHeader
          title="Maintenance Log"
          action={
            <Btn variant="secondary" size="sm" icon={Wrench}>Schedule</Btn>
          }
        />
        {maintenance.loading ? <AssetLoading /> : (
          <Table>
            <THead><TH>Type</TH><TH>Status</TH><TH>Scheduled</TH><TH>Completed</TH><TH>Technician</TH><TH right>Cost</TH></THead>
            <TBody>
              {!(maintenance.data?.content.length)
                ? <tr><td colSpan={6}><EmptyState icon={Wrench} title="No maintenance records" /></td></tr>
                : maintenance.data!.content.map((m) => (
                  <TR key={m.id}>
                    <TD><span className="rounded-lg bg-amber-50 px-2 py-0.5 text-xs font-semibold text-amber-700">{m.maintenanceType.replace(/_/g," ")}</span></TD>
                    <TD>
                      <span className={`rounded-full px-2 py-0.5 text-xs font-semibold
                        ${m.status==="COMPLETED"?"bg-emerald-100 text-emerald-700"
                        :m.status==="IN_PROGRESS"?"bg-blue-100 text-blue-700"
                        :"bg-slate-100 text-slate-500"}`}>
                        {m.status.replace(/_/g," ")}
                      </span>
                    </TD>
                    <TD muted>{fmtDate(m.scheduledDate)}</TD>
                    <TD muted>{fmtDate(m.completedDate)}</TD>
                    <TD muted>{m.technician ?? m.vendor ?? "—"}</TD>
                    <TD right muted>{m.cost != null ? fmt(m.cost) : "—"}</TD>
                  </TR>
                ))}
            </TBody>
          </Table>
        )}
      </Card>

      {/* Incident modal */}
      {incidentModal && (
        <IncidentModal
          type={incidentModal}
          assetName={data.name}
          onClose={() => setIncidentModal(null)}
          onSaved={() => { setIncidentModal(null); reload(); }}
        />
      )}
    </div>
  );
}

const inp = "w-full rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100";

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-semibold text-slate-500">{label}</span>
      {children}
    </label>
  );
}
